

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { EntityTerminology, formatEntityTerm } from '@scrambl/shared';
import { useOrganizationStore } from '@/store/organization.store';
import { useOrganizationSettingsQuery } from '@/hooks/queries/useOrganizationSettingsQuery';
import { useVisibleEntityTypesQuery } from '@/hooks/queries/useEntityTypesQuery';
import { createLogger } from '@/lib/logger';

const logger = createLogger('contexts/EntityTerminologyContext');

interface EntityTerminologyContextValue {
  // Raw terminology
  terminology: EntityTerminology;
  // Pre-computed formatted versions
  singular: string;
  plural: string;
  Singular: string;
  Plural: string;
  singularWithArticle: string;
  SingularWithArticle: string;
  
  // For edge cases where you need dynamic formatting
  format: (plural?: boolean, capitalize?: boolean, articlePrefix?: boolean) => string;
  
  // Loading state
  isLoading: boolean;
  
  // Current organization ID for cache invalidation
  organizationId?: string;
}

const defaultTerminology: EntityTerminology = {
  singular: 'client',
  plural: 'clients'
};

const EntityTerminologyContext = createContext<EntityTerminologyContextValue | null>(null);

// Cache for terminology by organization
const terminologyCache = new Map<string, EntityTerminology>();

export function EntityTerminologyProvider({ children }: { children: ReactNode }) {
  const { currentOrganization } = useOrganizationStore();
  const location = useLocation();
  
  // Don't fetch settings if we're on the organizations page
  const isOnOrganizationsPage = location.pathname === '/organizations';
  
  const queryEnabled = !!currentOrganization?.id && !isOnOrganizationsPage;
  
  const { data: settingsData, isLoading: settingsLoading } = useOrganizationSettingsQuery({
    enabled: queryEnabled,
  });

  const { data: entityTypes } = useVisibleEntityTypesQuery();

  const effectiveSettings = settingsData?.effectiveSettings;
  const [terminology, setTerminology] = useState<EntityTerminology>(defaultTerminology);
  const isLoading = settingsLoading;

  // Extract primitive values to avoid object reference changes.
  // Preference order:
  //   1. Organization setting (general.entityTerminology)
  //   2. Default entity type (isDefault=true)
  //   3. Sole visible entity type
  //   4. Hardcoded default "client/clients"
  const settingSingular = effectiveSettings?.general?.entityTerminology?.singular;
  const settingPlural = effectiveSettings?.general?.entityTerminology?.plural;

  const defaultType = entityTypes?.find(t => t.isDefault) ?? (entityTypes?.length === 1 ? entityTypes[0] : undefined);
  const typeSingular = defaultType?.name?.toLowerCase();
  const typePlural = (defaultType?.pluralName ?? defaultType?.name)?.toLowerCase();

  const terminologySingular = settingSingular || typeSingular;
  const terminologyPlural = settingPlural || typePlural;
  
  // Use ref to track previous values and prevent unnecessary updates
  const prevValuesRef = useRef({ singular: '', plural: '' });
  
  // Update terminology when settings change
  useEffect(() => {
    if (currentOrganization?.id && terminologySingular && terminologyPlural) {
      // Only update if values actually changed
      if (prevValuesRef.current.singular !== terminologySingular || 
          prevValuesRef.current.plural !== terminologyPlural) {
        prevValuesRef.current = { singular: terminologySingular, plural: terminologyPlural };
        const newTerminology = { singular: terminologySingular, plural: terminologyPlural };
        setTerminology(newTerminology);
        // Update cache
        terminologyCache.set(currentOrganization.id, newTerminology);
        // Update static helper
        updateStaticTerminology(currentOrganization.id, newTerminology);
      }
    } else if (!currentOrganization?.id || isOnOrganizationsPage) {
      // No organization context or on organizations page, use defaults
      if (prevValuesRef.current.singular !== defaultTerminology.singular || 
          prevValuesRef.current.plural !== defaultTerminology.plural) {
        prevValuesRef.current = defaultTerminology;
        setTerminology(defaultTerminology);
      }
    }
  }, [currentOrganization?.id, terminologySingular, terminologyPlural, isOnOrganizationsPage]);

  const { singular, plural } = terminology;
  const value: EntityTerminologyContextValue = {
    terminology,
    singular,
    plural,
    Singular: formatEntityTerm(singular, true, false),
    Plural: formatEntityTerm(plural, true, false),
    singularWithArticle: formatEntityTerm(singular, false, true),
    SingularWithArticle: formatEntityTerm(singular, true, true),
    format: (isPlural = false, capitalize = false, articlePrefix = false) => 
      formatEntityTerm(isPlural ? plural : singular, capitalize, articlePrefix),
    isLoading: isLoading || settingsLoading,
    organizationId: currentOrganization?.id,
  };

  return (
    <EntityTerminologyContext.Provider value={value}>
      {children}
    </EntityTerminologyContext.Provider>
  );
}

export function useEntityTerminology() {
  const context = useContext(EntityTerminologyContext);
  if (!context) {
    // In development, this might happen during hot module reloading or error recovery
    // Return a safe default instead of throwing to prevent cascading errors
    if (process.env.NODE_ENV === 'development') {
      logger.warn('useEntityTerminology called outside EntityTerminologyProvider - returning defaults');
      return {
        terminology: defaultTerminology,
        singular: defaultTerminology.singular,
        plural: defaultTerminology.plural,
        Singular: formatEntityTerm(defaultTerminology.singular, true, false),
        Plural: formatEntityTerm(defaultTerminology.plural, true, false),
        singularWithArticle: formatEntityTerm(defaultTerminology.singular, false, true),
        SingularWithArticle: formatEntityTerm(defaultTerminology.singular, true, true),
        format: (isPlural = false, capitalize = false, articlePrefix = false) => 
          formatEntityTerm(isPlural ? defaultTerminology.plural : defaultTerminology.singular, capitalize, articlePrefix),
        isLoading: false,
        organizationId: undefined,
      };
    }
    throw new Error('useEntityTerminology must be used within EntityTerminologyProvider');
  }
  return context;
}

// Static helper for places where hooks can't be used
// This maintains a map of terminology by organization ID
const staticTerminologyCache = new Map<string, EntityTerminology>();
let currentStaticOrgId: string | null = null;

export function updateStaticTerminology(organizationId: string, terminology: EntityTerminology) {
  staticTerminologyCache.set(organizationId, terminology);
  currentStaticOrgId = organizationId;
}

export function setCurrentStaticOrganization(organizationId: string | null) {
  currentStaticOrgId = organizationId;
}

export function getEntityTerminologyStatic(organizationId?: string): {
  singular: string;
  plural: string;
  Singular: string;
  Plural: string;
} {
  // Use provided org ID, current static org ID, or default
  const orgId = organizationId || currentStaticOrgId;
  const terminology = (orgId && staticTerminologyCache.get(orgId)) || defaultTerminology;
  
  return {
    singular: terminology.singular,
    plural: terminology.plural,
    Singular: terminology.singular.charAt(0).toUpperCase() + terminology.singular.slice(1),
    Plural: terminology.plural.charAt(0).toUpperCase() + terminology.plural.slice(1),
  };
}

// Helper to preload terminology for an organization
export async function preloadEntityTerminology(organizationId: string): Promise<void> {
  const currentOrg = useOrganizationStore.getState().currentOrganization;
  
  // If it's not the current org, we need to fetch its settings
  if (currentOrg?.id !== organizationId) {
    // This would require an API call to get the organization's settings
    // For now, we'll just use the default
    logger.warn(`Cannot preload terminology for non-current organization ${organizationId}`);
    return;
  }
  
  // If it is the current org, settings are automatically loaded by query
  // We just need to wait for the query to complete
  // Terminology will be cached by the provider
  const cached = terminologyCache.get(organizationId);
  if (cached) {
    updateStaticTerminology(organizationId, cached);
  }
}
